import React, { useState, ReactNode, useEffect } from 'react';

import {
    useFloating,
    offset,
    flip,
    shift,
    useHover,
    useClick,
    useFocus,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
    autoUpdate,
    safePolygon,
} from '@floating-ui/react';
import cn from 'classnames';

import { IS_DRAGGING_CLASS } from '@shared/model/globals';
import { useIsDragging } from '@shared/ui/UITooltip/lib/useIsDragging';

import cls from './UITooltip.module.css';

interface UITooltipProps {
    label: string | ReactNode;
    children: ReactNode;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    delay?: number;
    closeDelay?: number;
    classes?: string;
    tooltipClasses?: string;
    trigger?: 'hover' | 'click';
}

export const UITooltip = ({
    label,
    children,
    placement = 'top',
    delay = 150,
    closeDelay = 200,
    classes,
    tooltipClasses,
    trigger = 'hover',
}: UITooltipProps) => {
    const [open, setOpen] = useState(false);

    const isDragging = useIsDragging(IS_DRAGGING_CLASS);

    useEffect(() => {
        if (isDragging) setOpen(false);
    }, [isDragging]);

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement,
        middleware: [offset(6), flip(), shift()],
        whileElementsMounted: autoUpdate,
    });

    const hover = useHover(context, {
        enabled: trigger === 'hover' && !isDragging,
        move: false,
        delay: { open: delay, close: closeDelay },
        handleClose: safePolygon({ buffer: 2 }),
        detectPointerDown: false,
    });

    const click = useClick(context, {
        enabled: trigger === 'click' && !isDragging,
    });
    const focus = useFocus(context, { enabled: trigger === 'hover' });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: trigger === 'click' ? 'dialog' : 'tooltip' });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        click,
        focus,
        dismiss,
        role,
    ]);

    return (
        <>
            <span
                ref={refs.setReference}
                {...getReferenceProps()}
                className={cn(classes)}
                style={{ display: 'inline-block' }} // ← важно: не оставляем inline-текст без блока
            >
                {children}
            </span>

            <FloatingPortal>
                {open && (
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps({
                            onClick:
                                trigger === 'click'
                                    ? (event) => event.stopPropagation()
                                    : undefined,
                        })}
                        className={cn(cls.tooltip, [tooltipClasses])}
                    >
                        {label}
                    </div>
                )}
            </FloatingPortal>
        </>
    );
};
